import { useEffect } from 'react';

import { create } from 'zustand';

import type { Category } from 'constants/categories';
import { createApiClient } from 'lib/api/createApiClient';
import type { EventPositionVo } from 'models';
import { centsFromDollarsStringOrZero } from 'models/dollars';
import { numberFromFixedPointString } from 'models/fixedPoint';
import type { MarketVo } from 'models/market';
import type { PortfolioEvent, PortfolioMarket, PortfolioMarketPosition } from 'models/portfolioEvent';
import type { GetMarketResponse } from 'models/reqres';
import type { PositionDTO, TickerDTO, WsMessage, WsResponse } from 'models/ws';
import { toAdvancedPortfolioCSV, type AdvancedPortfolioCSV } from 'queries/portfolioCsv';
import { fetchOpenEventsByTickers } from 'queries/portfolioEvents';
import {
    applyMarketDerivedPositionFields,
    applyTickerToPortfolioMarket,
    marketVoToPortfolioMarket,
    positionDtoToVo,
    positionVoToPortfolioPosition,
} from 'queries/portfolioEventTransform';
import { useUserProperties } from 'queries/userProperties';
import { useSubAccountStore } from 'stores/zustand/subAccountStore';
import { legacyExchangeWsClient } from 'transport/wsClients';
import config from 'utils/config';

import { useAuthStore } from '../../lib/stores/authStore';
import { subscribeToRawPosition } from '../../lib/stores/userPositionsStore';

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

interface PortfolioEventsActions {
    /**
     * Replace the open bucket and the canonical full-list of open event
     * tickers. `tickers` is the post-sort/filter list returned on page 1
     * (covers every open event the server knows about across pages), used
     * by the open-positions infinite scroll and as the "did the server
     * know about this event" check inside `positionWsUpdated`.
     */
    openEventsLoaded: (events: PortfolioEvent[], tickers: string[]) => void;
    /**
     * Prepend a brand-new open event to `openEvents` and add its ticker
     * to `openEventTickers`. Called from `usePortfolioUserPositionSync`
     * after fetching event metadata for a `user_position` WS payload
     * whose event was not in `openEventTickers` (i.e. a position the
     * user just opened on an event the server hasn't seen yet).
     *
     * Idempotent: skips when the event is already loaded.
     */
    openEventInserted: (event: PortfolioEvent) => void;
    pendingEventsLoaded: (pending: PortfolioEvent[], earlyExits: PortfolioEvent[]) => void;
    pendingSearchEventsLoaded: (pending: PortfolioEvent[]) => void;
    nonSearchOpenPendingCountsLoaded: (openCount: number, pendingCount: number) => void;
    settledEventsLoaded: (events: PortfolioEvent[], cursor: string | undefined) => void;
    /**
     * Replace the list of categories that have at least one open or pending
     * position. Computed server-side over the unfiltered position lists, so
     * this is independent of the active category filter — switching filters
     * doesn't make pills disappear.
     */
    availableCategoriesLoaded: (categories: Category[]) => void;
    /**
     * Update the free-text search term that narrows the open + pending
     * portfolio buckets. The actual filtering happens server-side via the
     * `/event-positions` route — this action just pins the canonical value
     * so the search input and the React Query hook stay in sync.
     */
    searchTermChanged: (searchTerm: string) => void;
    openLoadingChanged: (loading: boolean) => void;
    pendingLoadingChanged: (loading: boolean) => void;
    settledLoadingChanged: (loading: boolean) => void;
    updateClosedTabCounts: (
        eventPositions: EventPositionVo[],
        knownMarketIds: string[],
        persistMarketIds: (ids: string[]) => void,
    ) => void;
    resetClosedTabCounts: () => void;
    /**
     * Apply a `user_position` WebSocket delta to the already-fetched buckets.
     * Updates in-place when the event+market is present; drops the entry
     * from the open bucket when the position settles or returns to zero.
     *
     * Brand-new events / markets are backfilled by
     * `usePortfolioUserPositionSync`:
     *   - event missing from the server-side `openEventTickers` list →
     *     fetch the full event and prepend via `openEventInserted`;
     *   - event present but market missing → fetch the market and insert
     *     via `positionMarketInserted`.
     */
    positionWsUpdated: (payload: PositionDTO) => void;
    /**
     * Insert a newly-fetched market into every bucket that already holds its
     * event and whose bucket rule accepts the position from `dto`. Called
     * after `usePortfolioUserPositionSync` detects a WS update for a market
     * that wasn't present in the initial fetch.
     *
     * Idempotent: skips insertion when the market is already in the bucket.
     */
    positionMarketInserted: (eventTicker: string, dto: PositionDTO, marketVo: MarketVo) => void;
    /**
     * Apply a `ticker` WebSocket delta to every bucket entry whose market
     * matches `payload.market_id`. The `PortfolioMarket` snapshot is
     * patched with the streamed price/volume/open-interest fields, and
     * the `PortfolioPosition` is recomputed so market-derived numbers
     * (`marketValueInDollars`, the unrealized/total return numbers, the
     * period price-change deltas, etc.) tick in lockstep with the price.
     */
    tickerWsUpdated: (payload: TickerDTO) => void;
    /**
     * Apply a batch of `ticker` WebSocket deltas in a single Zustand set()
     * call. Accepts the latest payload per market_id after a throttle window
     * so one React render handles all price updates from that window instead
     * of one render per tick.
     */
    tickersBatchUpdated: (payloads: TickerDTO[]) => void;
    reset: () => void;
}

export interface PortfolioEventsState {
    /** Open positions, including combo / MVE events inline. */
    openEvents: PortfolioEvent[];
    /**
     * Full ordered list of open event tickers post-sort/filter
     * (server-side). Drives the open-positions infinite-scroll and acts
     * as the canonical "did the server know about this event" set used
     * by `positionWsUpdated` to decide whether a brand-new
     * `user_position` payload should prepend a new row or wait for the
     * next paginated fetch to surface the event.
     */
    openEventTickers: string[];
    /** Pending (close+unsettled) positions — markets pending payout/settlement. */
    pendingEvents: PortfolioEvent[];
    /**
     * Early-exit slice produced by the pending fetch. These belong in the
     * history view because the user fully exited the position before the
     * market settled.
     */
    earlyExitEvents: PortfolioEvent[];
    /** Settled history positions (paginated, accumulates across pages). */
    settledEvents: PortfolioEvent[];

    /**
     * Categories present across all open + pending positions. Driven by the
     * server-side response so the header's filter pills don't have to walk
     * the (possibly category-filtered) bucket arrays themselves.
     */
    availableCategories: Category[];
    /**
     * Counts from the non-search open + pending response. Search results reuse
     * the rendered buckets, so these preserve whether the base Positions tab
     * has data while an empty search is active or being cleared.
     */
    nonSearchOpenCount: number;
    nonSearchPendingCount: number;

    /**
     * Active free-text search applied to open + pending portfolio events
     * (plain raw value — debouncing happens at the input layer before this
     * is set so the React Query key only flips once per debounce window).
     */
    searchTerm: string;

    isOpenLoading: boolean;
    isPendingLoading: boolean;
    isSettledLoading: boolean;

    /** Cursor for the next page of settled history events (`undefined` = no more pages). */
    settledCursor: string | undefined;

    /** Count of newly-closed markets seen since the user last visited the History tab. */
    pendingClosedCount: number;
    /** Count of newly-closed markets with a positive realized PnL (wins). */
    pendingClosedWins: number;

    actions: PortfolioEventsActions;
}

const initialState = {
    openEvents: [] as PortfolioEvent[],
    openEventTickers: [] as string[],
    pendingEvents: [] as PortfolioEvent[],
    earlyExitEvents: [] as PortfolioEvent[],
    settledEvents: [] as PortfolioEvent[],
    availableCategories: [] as Category[],
    nonSearchOpenCount: 0,
    nonSearchPendingCount: 0,
    searchTerm: '',
    isOpenLoading: false,
    isPendingLoading: false,
    isSettledLoading: false,
    settledCursor: undefined as string | undefined,
    pendingClosedCount: 0,
    pendingClosedWins: 0,
};

interface BucketUpdate {
    /** Whether the WS-updated entry should remain in the bucket. */
    keep: (next: { positionVal: number; isSettled: boolean }) => boolean;
}

const bucketRules: Record<'open' | 'pending' | 'earlyExits', BucketUpdate> = {
    open: { keep: ({ positionVal, isSettled }) => Math.abs(positionVal) > 0 && !isSettled },
    pending: { keep: ({ isSettled }) => !isSettled },
    earlyExits: { keep: ({ isSettled }) => !isSettled },
};

/**
 * Result of a single bucket-entry patch: either a replacement entry, or
 * `null` to drop the entry from the bucket. Used by `applyDtoToBucket` to
 * keep the find/clone/cleanup plumbing in one place while letting each WS
 * handler decide what to write — position updates flow through
 * `positionDtoPatcher`, ticker updates through `tickerDtoPatcher`, and
 * future DTOs can add their own patcher without touching the bucket
 * traversal logic.
 */
type BucketEntryPatch = (entry: PortfolioMarketPosition) => PortfolioMarketPosition | null;

/**
 * Locate the entry matching `eventTicker` (when supplied) + `marketId` in
 * `events`, hand it to `patch`, and rebuild the bucket immutably around
 * the patched / dropped result. When `eventTicker` is `undefined` the
 * search falls back to scanning every event for the first market whose
 * id matches — needed because `TickerDTO` does not carry the event
 * ticker.
 *
 * Returns `mutated: false` when no matching entry was found, so the store
 * can short-circuit the `set` call and avoid spurious re-renders.
 */
const applyDtoToBucket = (
    events: PortfolioEvent[],
    matcher: { eventTicker?: string; marketId: string },
    patch: BucketEntryPatch,
): { events: PortfolioEvent[]; mutated: boolean } => {
    const eventIdx = matcher.eventTicker
        ? events.findIndex((e) => e.eventTicker === matcher.eventTicker)
        : events.findIndex((e) => e.markets.some(({ market }) => market.id === matcher.marketId));
    if (eventIdx === -1) return { events, mutated: false };

    const event = events[eventIdx];
    const marketIdx = event.markets.findIndex(({ market }) => market.id === matcher.marketId);
    if (marketIdx === -1) return { events, mutated: false };

    const next = patch(event.markets[marketIdx]);

    const nextMarkets = [...event.markets];
    if (next === null) {
        nextMarkets.splice(marketIdx, 1);
    } else {
        nextMarkets[marketIdx] = next;
    }

    if (nextMarkets.length === 0) {
        const nextEvents = [...events];
        nextEvents.splice(eventIdx, 1);
        return { events: nextEvents, mutated: true };
    }

    const nextEvents = [...events];
    nextEvents[eventIdx] = { ...event, markets: nextMarkets };
    return { events: nextEvents, mutated: true };
};

const positionDtoPatcher =
    (dto: PositionDTO, bucket: 'open' | 'pending' | 'earlyExits'): BucketEntryPatch =>
    (existing) => {
        const positionVal = numberFromFixedPointString(dto.position_fp);
        if (!bucketRules[bucket].keep({ positionVal, isSettled: dto.is_settled })) return null;
        const nextPosition = positionVoToPortfolioPosition(positionDtoToVo(dto), existing.market);
        return { market: existing.market, position: nextPosition };
    };

/**
 * Insert a market derived from `marketVo` + `dto` into `events` when the
 * event is present but the market is missing. Returns `mutated: false` when
 * the event is absent, the market is already present, or the bucket rule
 * rejects the position — making repeated calls safe (idempotent).
 */
const applyMarketInsertToBucket = (
    events: PortfolioEvent[],
    eventTicker: string,
    dto: PositionDTO,
    marketVo: MarketVo,
    bucket: 'open' | 'pending' | 'earlyExits',
): { events: PortfolioEvent[]; mutated: boolean } => {
    const positionVal = numberFromFixedPointString(dto.position_fp);
    const eventIdx = events.findIndex((e) => e.eventTicker === eventTicker);
    if (eventIdx === -1) return { events, mutated: false };
    const event = events[eventIdx];
    if (event.markets.some((m) => m.market.id === dto.market_id)) return { events, mutated: false };
    if (!bucketRules[bucket].keep({ positionVal, isSettled: dto.is_settled })) return { events, mutated: false };

    // event.markets.length ≥ 1 (event can't exist in store with 0 markets),
    // so total with the new market ≥ 2 → isTrueBinary = false.
    // Only `markets.length` is read by `marketVoToPortfolioMarket` to derive isTrueBinary.
    const eventCtx = {
        ticker: event.eventTicker,
        series_ticker: event.seriesTicker,
        markets: Array(event.markets.length + 1),
    };
    const market = marketVoToPortfolioMarket(marketVo, eventCtx);
    const position = positionVoToPortfolioPosition(positionDtoToVo(dto), market);
    const nextMarkets = [...event.markets, { market, position }];
    const nextEvents = [...events];
    nextEvents[eventIdx] = { ...event, markets: nextMarkets };
    return { events: nextEvents, mutated: true };
};

const tickerDtoPatcher =
    (dto: TickerDTO): BucketEntryPatch =>
    (existing) => {
        const nextMarket: PortfolioMarket = applyTickerToPortfolioMarket(existing.market, dto);
        // `lastPrice` (and friends) feed back into `PortfolioPosition`'s
        // market-value / unrealized-return / period-delta numbers; recompute
        // those off the patched market so the row's PnL ticks alongside the
        // price.
        const nextPosition = applyMarketDerivedPositionFields(existing.position, nextMarket);
        return { market: nextMarket, position: nextPosition };
    };

export const usePortfolioEventsStore = create<PortfolioEventsState>()((set) => ({
    ...initialState,
    actions: {
        openEventsLoaded: (events, tickers) => set({ openEvents: events, openEventTickers: tickers }),
        openEventInserted: (event) =>
            set((state) => {
                // Idempotent — concurrent WS + React Query refetches can
                // race; the second writer must not double-insert.
                if (state.openEvents.some((e) => e.eventTicker === event.eventTicker)) return state;
                return {
                    openEvents: [event, ...state.openEvents],
                    openEventTickers: state.openEventTickers.includes(event.eventTicker)
                        ? state.openEventTickers
                        : [event.eventTicker, ...state.openEventTickers],
                };
            }),
        pendingEventsLoaded: (pending, earlyExits) => set({ pendingEvents: pending, earlyExitEvents: earlyExits }),
        pendingSearchEventsLoaded: (pending) => set({ pendingEvents: pending }),
        nonSearchOpenPendingCountsLoaded: (openCount, pendingCount) =>
            set({ nonSearchOpenCount: openCount, nonSearchPendingCount: pendingCount }),
        settledEventsLoaded: (events, cursor) => set({ settledEvents: events, settledCursor: cursor }),
        availableCategoriesLoaded: (categoriesList) => set({ availableCategories: categoriesList }),
        searchTermChanged: (searchTerm) => set({ searchTerm }),
        openLoadingChanged: (loading) => set({ isOpenLoading: loading }),
        pendingLoadingChanged: (loading) => set({ isPendingLoading: loading }),
        settledLoadingChanged: (loading) => set({ isSettledLoading: loading }),
        updateClosedTabCounts: (eventPositions, knownMarketIds, persistMarketIds) => {
            const searchedMarketIds: string[] = [];
            for (const ep of eventPositions) {
                for (const mp of ep.market_positions) {
                    searchedMarketIds.push(mp.market_id);
                }
            }

            const isKnownEmpty = knownMarketIds.length === 0;
            let newCount = 0;
            for (const marketId of searchedMarketIds) {
                if (!knownMarketIds.includes(marketId)) newCount++;
            }

            if ((!isKnownEmpty && newCount > 0) || (isKnownEmpty && newCount === 1)) {
                const recentlyClosed = searchedMarketIds.filter((id) => !knownMarketIds.includes(id));
                let winsCount = 0;
                for (const ep of eventPositions) {
                    for (const mp of ep.market_positions) {
                        if (
                            recentlyClosed.includes(mp.market_id) &&
                            centsFromDollarsStringOrZero(mp.realized_pnl_dollars) > 0
                        ) {
                            winsCount++;
                        }
                    }
                }
                set({ pendingClosedCount: newCount, pendingClosedWins: winsCount });
            }

            persistMarketIds(searchedMarketIds);
        },
        resetClosedTabCounts: () => set({ pendingClosedCount: 0, pendingClosedWins: 0 }),
        positionWsUpdated: (payload) => {
            if (!payload.event_ticker || !payload.market_id) return;

            set((state) => {
                const partial: Partial<PortfolioEventsState> = {};
                const matcher = { eventTicker: payload.event_ticker, marketId: payload.market_id };

                const open = applyDtoToBucket(state.openEvents, matcher, positionDtoPatcher(payload, 'open'));
                if (open.mutated) partial.openEvents = open.events;

                const pending = applyDtoToBucket(state.pendingEvents, matcher, positionDtoPatcher(payload, 'pending'));
                if (pending.mutated) partial.pendingEvents = pending.events;

                const earlyExits = applyDtoToBucket(
                    state.earlyExitEvents,
                    matcher,
                    positionDtoPatcher(payload, 'earlyExits'),
                );
                if (earlyExits.mutated) partial.earlyExitEvents = earlyExits.events;

                return partial;
            });
        },
        positionMarketInserted: (eventTicker, dto, marketVo) => {
            set((state) => {
                const partial: Partial<PortfolioEventsState> = {};

                const open = applyMarketInsertToBucket(state.openEvents, eventTicker, dto, marketVo, 'open');
                if (open.mutated) partial.openEvents = open.events;

                const pending = applyMarketInsertToBucket(state.pendingEvents, eventTicker, dto, marketVo, 'pending');
                if (pending.mutated) partial.pendingEvents = pending.events;

                const earlyExits = applyMarketInsertToBucket(
                    state.earlyExitEvents,
                    eventTicker,
                    dto,
                    marketVo,
                    'earlyExits',
                );
                if (earlyExits.mutated) partial.earlyExitEvents = earlyExits.events;

                return partial;
            });
        },
        tickerWsUpdated: (payload) => {
            if (!payload.market_id) return;

            set((state) => {
                const partial: Partial<PortfolioEventsState> = {};
                // `TickerDTO` does not carry `event_ticker`, so let the
                // helper scan each bucket for the first event containing
                // a matching market.
                const matcher = { marketId: payload.market_id };
                const patcher = tickerDtoPatcher(payload);

                const open = applyDtoToBucket(state.openEvents, matcher, patcher);
                if (open.mutated) partial.openEvents = open.events;

                const pending = applyDtoToBucket(state.pendingEvents, matcher, patcher);
                if (pending.mutated) partial.pendingEvents = pending.events;

                const earlyExits = applyDtoToBucket(state.earlyExitEvents, matcher, patcher);
                if (earlyExits.mutated) partial.earlyExitEvents = earlyExits.events;

                // `settledEvents` is intentionally excluded: settled markets
                // have a final price and won't receive further ticker updates,
                // and the bucket can grow unbounded — scanning it on every tick
                // would be pure overhead for a high-frequency channel.

                // High-frequency channel — bail out of the merge entirely
                // when none of the buckets carry the streamed market, so
                // we don't churn out a fresh state object on every tick
                // for unrelated markets.
                if (!open.mutated && !pending.mutated && !earlyExits.mutated) return state;

                return partial;
            });
        },
        tickersBatchUpdated: (payloads) => {
            if (payloads.length === 0) return;
            set((state) => {
                let openEvents = state.openEvents;
                let pendingEvents = state.pendingEvents;
                let earlyExitEvents = state.earlyExitEvents;
                let anyMutated = false;

                for (const payload of payloads) {
                    if (!payload.market_id) continue;
                    const matcher = { marketId: payload.market_id };
                    const patcher = tickerDtoPatcher(payload);

                    const open = applyDtoToBucket(openEvents, matcher, patcher);
                    if (open.mutated) {
                        openEvents = open.events;
                        anyMutated = true;
                    }

                    const pending = applyDtoToBucket(pendingEvents, matcher, patcher);
                    if (pending.mutated) {
                        pendingEvents = pending.events;
                        anyMutated = true;
                    }

                    const earlyExits = applyDtoToBucket(earlyExitEvents, matcher, patcher);
                    if (earlyExits.mutated) {
                        earlyExitEvents = earlyExits.events;
                        anyMutated = true;
                    }
                }

                if (!anyMutated) return state;
                return { openEvents, pendingEvents, earlyExitEvents };
            });
        },
        reset: () => set(initialState),
    },
}));

// ---- WebSocket subscription -------------------------------------------
//
// Subscribe once on first import (browser only) to two channels:
//
//   - `cdc_qe_user_positions` (gated on auth) — `user_position` payloads
//     update the `PortfolioPosition` half of bucket entries via
//     `positionWsUpdated`. Adding a brand-new market still requires the
//     next React Query refetch because the DTO doesn't carry market/event
//     metadata.
//   - `ticker` (always-on, no market filter) — `ticker` payloads patch
//     the `PortfolioMarket` snapshot (price/volume/open-interest) and
//     recompute the market-derived `PortfolioPosition` fields via
//     `tickerWsUpdated`, so the table cells track WS price updates the
//     same way the live `BinaryMarket` does.

/**
 * Wires the Zustand portfolio store's ticker updates to the MarketStore's
 * existing ticker feed instead of opening a second all-market subscription.
 * Incoming payloads are buffered in a Map (deduplicating by market_id so only
 * the latest ticker per market survives each window) and flushed into a single
 * Zustand set() call every second. This keeps the portfolio table from
 * re-rendering on every individual price tick — on liquid markets that can
 * arrive tens of times per second, which saturates the main thread on mobile.
 * Call this once at the portfolio route root.
 */
export function usePortfolioTickerSync(): void {
    useEffect(() => {
        const pending = new Map<string, TickerDTO>();

        const unsubscribe = legacyExchangeWsClient.subscribe(
            { channels: ['ticker'], market_ids: [] },
            (response: WsResponse) => {
                const messages = typeof response === 'object' ? [response as WsMessage] : (response as WsMessage[]);
                for (const msg of messages) {
                    if (msg.type === 'ticker') {
                        const payload = msg.msg as TickerDTO;
                        if (payload.market_id) pending.set(payload.market_id, payload);
                    }
                }
            },
        );

        const interval = setInterval(
            () => {
                if (pending.size === 0) return;
                const payloads = Array.from(pending.values());
                pending.clear();
                usePortfolioEventsStore.getState().actions.tickersBatchUpdated(payloads);
            },
            1000,
            undefined,
        );

        return () => {
            unsubscribe();
            clearInterval(interval);
        };
    }, []);
}

/**
 * Wires user-position WS updates through `userPositionsStore`'s shared raw
 * position feed instead of opening a second connection. Also resets portfolio
 * state on logout so stale positions don't persist across sessions.
 * Call this once at the portfolio route root.
 *
 * Three escalating backfill paths run after every `positionWsUpdated`:
 *
 *   1. The DTO references an event the server-side ticker list doesn't
 *      include — the user just opened a position on a brand-new event.
 *      Fetch the full `PortfolioEvent` (event metadata + all market
 *      positions) via the same ticker route the open-list pagination uses,
 *      then prepend it via `openEventInserted`. Skips the per-market fetch
 *      below because the full-event fetch already carries the market data.
 *   2. The event is known but the market id isn't in the bucket — fetch
 *      just the market by ticker and backfill it via
 *      `positionMarketInserted`.
 *   3. Both event and market are present — `positionWsUpdated` already
 *      mutated the entry in place; nothing else to do.
 */
export function usePortfolioUserPositionSync(): void {
    const userId = useAuthStore((s) => s.userId);
    const subaccountNumber = useSubAccountStore((s) => s.subaccountNumber);
    useEffect(() => {
        if (!userId) {
            usePortfolioEventsStore.getState().actions.reset();
            return;
        }
        const marketsFetching = new Set<string>();
        const eventsFetching = new Set<string>();
        const controller = new AbortController();
        let active = true;
        const isCurrentScope = (): boolean =>
            active &&
            useAuthStore.getState().userId === userId &&
            useSubAccountStore.getState().subaccountNumber === subaccountNumber;
        const unsubscribe = subscribeToRawPosition((payload) => {
            if (!isCurrentScope()) return;
            const { event_ticker, market_id, market_ticker } = payload;
            const positionVal = numberFromFixedPointString(payload.position_fp);
            const isOpenPosition = Math.abs(positionVal) > 0 && !payload.is_settled;

            // Check BEFORE applying the update so we don't conflate a
            // market that `positionWsUpdated` just removed with a market
            // we never had — both leave the event without a matching
            // entry, but only the latter warrants a fetch.
            const s = usePortfolioEventsStore.getState();

            // Path 1: brand-new open event the server hasn't surfaced
            // yet. The canonical ticker list is the full server-side
            // ordering (not just the loaded subset), so we don't mistake
            // an event that's just on a future page for a brand-new one.
            const isNewOpenEvent =
                isOpenPosition &&
                !!event_ticker &&
                !s.openEventTickers.includes(event_ticker) &&
                !eventsFetching.has(event_ticker);

            // Path 2: known event missing this specific market.
            // `settledEvents` is excluded intentionally — settled positions
            // don't receive new `user_position` WS updates, so a missing
            // market there will never need backfilling via this path.
            const needsMarketFetch =
                !isNewOpenEvent &&
                !!event_ticker &&
                !!market_id &&
                !!market_ticker &&
                !marketsFetching.has(market_id) &&
                [s.openEvents, s.pendingEvents, s.earlyExitEvents].some((bucket) =>
                    bucket.some(
                        (event) =>
                            event.eventTicker === event_ticker && !event.markets.some((m) => m.market.id === market_id),
                    ),
                );

            usePortfolioEventsStore.getState().actions.positionWsUpdated(payload);

            if (isNewOpenEvent && event_ticker) {
                eventsFetching.add(event_ticker);
                fetchOpenEventsByTickers(userId, [event_ticker], subaccountNumber, controller.signal)
                    .then((events) => {
                        const inserted = events[0];
                        if (!inserted || !isCurrentScope()) return;
                        usePortfolioEventsStore.getState().actions.openEventInserted(inserted);
                    })
                    .catch(() => {
                        // next React Query refetch will surface the event
                    })
                    .finally(() => {
                        eventsFetching.delete(event_ticker);
                    });
                return;
            }

            if (!needsMarketFetch || !event_ticker || !market_id || !market_ticker) return;
            marketsFetching.add(market_id);
            api.get<GetMarketResponse>(`/markets_by_ticker/${market_ticker}`, { signal: controller.signal })
                .then((response) => {
                    if (!isCurrentScope()) return;
                    usePortfolioEventsStore
                        .getState()
                        .actions.positionMarketInserted(event_ticker, payload, response.data.market);
                })
                .catch(() => {
                    // next React Query refetch will add the market
                })
                .finally(() => {
                    marketsFetching.delete(market_id);
                });
        });
        return () => {
            active = false;
            controller.abort();
            unsubscribe();
        };
    }, [userId, subaccountNumber]);
}

/**
 * Build the advanced-portfolio CSV rows from the current open-events bucket.
 * Lives here (rather than on `MarketStore`) because the source of truth for
 * portfolio positions has moved to this store. The user's market-price
 * preference comes from the React Query `userProperties` cache — the events
 * store doesn't carry it.
 *
 * Returns one row per `(event, market)` pair in the open bucket.
 */
export const useAdvancedPortfolioCSV = (): AdvancedPortfolioCSV[] => {
    const openEvents = usePortfolioEventsStore((s) => s.openEvents);
    const {
        properties: { marketCardPriceType },
    } = useUserProperties();

    return openEvents.flatMap((event) => {
        return event.markets.map(({ market, position }) =>
            toAdvancedPortfolioCSV(market, position, {
                eventTitle: event.title,
                marketCardPriceType,
            }),
        );
    });
};
